/**
 * Gratitude pages — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_desk_paper`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const gratitudePagesPack: ToilePack = {
    slug: 'gratitude-pages',
    category: 'planner',
    name: 'Gratitude pages',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'daily', label: 'Day page', subject: 'a day-to-a-page sheet with a small line drawing of a steaming cup at the top and three numbered ruled lines beneath it'},
        {key: 'weekly', label: 'Week page', subject: 'a week-to-a-page grid of seven small boxes with one ruled line in each, and a window with a potted plant drawn small in the corner'},
        {key: 'night', label: 'Night page', subject: 'a bedtime sheet with a moon over a roofline drawn small at the top, three ruled lines, and an empty square for one little drawing'},
    ],
    styles: [
        {key: 'quiet', label: 'Quiet', style: 'a spare minimalist layout, hairline rules, one light sans-serif and a great deal of empty paper', palette: 'black ink on plain white paper', framing: 'centered on a vertical page with wide even margins, print-ready'},
        {key: 'warm', label: 'Warm', style: 'a warm botanical treatment, a fine ink eucalyptus sprig trailing down one side and a soft tone block behind the header', palette: 'sage green and warm terracotta on cream paper', framing: 'laid out on a vertical page with a generous outer margin'},
        {key: 'cheerful', label: 'Cheerful', style: 'a bright cheerful version for children, chunky rounded shapes, a dashed border and a small star to color beside every line', palette: 'sunny yellow, coral and sky blue on white', framing: 'filling the page inside a thick playful border with a clear margin'},
    ],
    directions: ['for kids at bedtime', 'with room to draw', 'one card a day'],
    seo: {
        title: 'Gratitude journal printable: day, week or night',
        metaDescription: 'Nine gratitude pages — a morning cup, a windowsill plant, a bedtime moon — drawn quiet, warm, and cheerful, for a daily practice.',
        h1: 'Gratitude journal printable: day, week or bedtime page',
        tagline: 'Nine gratitude pages, three daily rhythms, three treatments each — the header holds, the drawing changes.',
        intro: 'A gratitude page holds three lines under one header: today I am grateful for. These nine keep that header exact across a day page, a week page, and a bedtime page, drawn quiet and minimal, warm and botanical, or bright and cheerful for kids. Print a stack and fill one in every morning, or every night.',
        useCases: [
            'a daily gratitude page for a journal',
            'a bedtime gratitude ritual for kids',
            'a week-to-a-page insert for a planner',
            'a calming morning routine sheet',
        ],
        faq: [
            {q: 'What size does the page print at?', a: 'Portrait A4 proportions (832 by 1216) with an even margin, ready for a journal or a binder.'},
            {q: 'Is the writing space filled in?', a: 'No — every ruled line stays blank so you write your own entries by hand.'},
            {q: 'Is the cheerful version suitable for young children?', a: "Yes — it's drawn with chunky shapes and a star to color, no other text beyond the fixed header."},
            {q: 'Can I get a card-sized version?', a: 'Yes — the direction field lets you ask for a pocket-size card instead of a full page.'},
        ],
    },
}
